import { Box, Button, Chip, Divider, Typography } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useTranslation } from 'react-i18next';
import { NotificationAPI } from 'src/services/api/notificationAPI';
import { useGlobals } from 'src/services/contexts/appContext';
import ConsoleHelper from 'src/services/helpers/consoleHelper';
import { useEffect, useState } from 'react';
import { NotificationResponse } from 'src/services/api/response/notificationResponse';
import { useQueryClient } from '@tanstack/react-query';
import { NotificationListItem } from './notificationListItem';

export const NotificationList = () => {
  const queryClient = useQueryClient();
  const { unreadNotifications, notifications } = useGlobals();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(true);
  const [filteredNotifications, setFilteredNotifications] = useState<NotificationResponse[] | undefined>(undefined);

  const markAllNotificationsAsRead = async () => {
    const API = new NotificationAPI();
    setLoading(true);
    try {
      await API.SetAllNotificationReadStatus();
      queryClient.invalidateQueries(['notifications']);
      setLoading(false);
    } catch (err) {
      ConsoleHelper.log('Error: Marking all notifications as read');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showAll) {
      setFilteredNotifications(notifications);
    } else {
      setFilteredNotifications(unreadNotifications);
    }
  }, [notifications, showAll, unreadNotifications]);

  return (
    <Box sx={{ minWidth: 320 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2, pb: 1 }}>
        <Typography variant='h6' sx={{ fontWeight: 600 }}>
          {t('common:notifications')}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Chip
            label={t('common:all')}
            size='small'
            variant={showAll ? 'filled' : 'outlined'}
            color={showAll ? 'primary' : 'default'}
            onClick={() => setShowAll(true)}
            disabled={loading}
          />
          <Chip
            label={t('common:unread')}
            size='small'
            variant={!showAll ? 'filled' : 'outlined'}
            color={!showAll ? 'primary' : 'default'}
            onClick={() => setShowAll(false)}
            disabled={loading}
          />
        </Box>
        <Button
          disabled={loading}
          size='small'
          endIcon={<DoneAllIcon fontSize='small' />}
          onClick={() => markAllNotificationsAsRead()}
          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
        >
          {t('common:markAllAsRead')}
        </Button>
      </Box>

      <Divider />

      <Box>
        {filteredNotifications && filteredNotifications?.length > 0 ? (
          filteredNotifications.map((item) => <NotificationListItem key={item.ID} item={item} />)
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              {t('common:noUnreadMessages')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
